import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./transformation.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";
import ReactQuill from "react-quill";
import parse from "html-react-parser";

interface User {
  _id: string;
  title: string;
  description: string;
  beforeImageUrl: string;
  afterImageUrl: string;
  createdAt: Date;
}

const Transformations = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [showCreateBlogOverlay, setShowCreateBlogOverlay] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [trasformationsDetails, setTransformationsDetails] = useState({
    title: "",
    description: "",
  });
  const [beforeImageUrl, setBeforeImageFile] = useState<File | null>(null); // For image upload
  const [afterImageUrl, setAfterImageFile] = useState<File | null>(null); // For image upload

  const navigate = useNavigate();
  const isInitialRender = useRef(true);

  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllTransformations();
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      alert("Session expired or invalid token! Redirecting to login.");
      handleLogout();
    }
  };

  const fetchAllTransformations = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-transformations`
      );
      setUsers(response.data.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  const handleCreateTransformation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !trasformationsDetails.title ||
      !trasformationsDetails.description ||
      !beforeImageUrl
    ) {
      toast.error("Please fill in all the required fields!");
      return;
    }

    const formData = new FormData();
    formData.append("title", trasformationsDetails.title);
    formData.append("description", trasformationsDetails.description);
    formData.append("beforeImage", beforeImageUrl as File);
    formData.append("afterImage", afterImageUrl as File);

    try {
      const response = await axios.post(
        "https://api.fitoja.in/api/create-transformation",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.data.success) {
        toast.success("Blog created successfully!");
        setShowCreateBlogOverlay(false);
        fetchAllTransformations(); // Refresh blog list
      }
    } catch (error) {
      console.error("Failed to create blog:", error);
      toast.error("Failed to create blog. Please try again.");
    }
  };

  const filteredUsers = users.filter((user) =>
    user.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
  );

  return (
    <div className="transformations">
      <ToastContainer theme="colored" />
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}
      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} alt="Logo" />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                  alt="Close"
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          <div className="search-box">
            <h2>Transformations</h2>
            <input
              type="text"
              placeholder="Search transformation by Title"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div
            className="create-blog-btn"
            onClick={() => setShowCreateBlogOverlay(true)}
          >
            <span>Create new Transformation</span>
          </div>

          {showCreateBlogOverlay && (
            <div className="create-blog-overlay">
              <div className="create-blog-content">
                <h3>Create New Transformation</h3>
                <form
                  style={{ display: "grid", gap: "10px", alignItems: "start" }}
                  onSubmit={handleCreateTransformation}
                >
                  <div>
                    <label>Title</label>
                    <input
                      type="text"
                      value={trasformationsDetails.title}
                      onChange={(e) =>
                        setTransformationsDetails({
                          ...trasformationsDetails,
                          title: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div>
                    <label>Description</label>
                    <ReactQuill
                      value={trasformationsDetails.description}
                      onChange={(value) =>
                        setTransformationsDetails({
                          ...trasformationsDetails,
                          description: value,
                        })
                      }
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, 4, 5, 6, false] }],
                          ["bold", "italic", "underline"],
                          [{ list: "ordered" }, { list: "bullet" }],
                          [{ align: [] }],
                          ["image"],
                          ["clean"], // Remove formatting
                        ],
                      }}
                      formats={[
                        "header",
                        "bold",
                        "italic",
                        "underline",
                        "blockquote",
                        "code-block",
                        "list",
                        "bullet",
                        "align",
                        "link",
                        "image",
                      ]}
                    />
                  </div>
                  <div>
                    <label>Upload Before Image</label>
                    <input
                      type="file"
                      name="beforeImageUrl"
                      onChange={(e) =>
                        setBeforeImageFile(
                          e.target.files ? e.target.files[0] : null
                        )
                      }
                      required
                    />
                  </div>
                  <div>
                    <label>Upload After Image</label>
                    <input
                      type="file"
                      name="afterImageUrl"
                      onChange={(e) =>
                        setAfterImageFile(
                          e.target.files ? e.target.files[0] : null
                        )
                      }
                      required
                    />
                  </div>
                  <br></br>
                  <button type="submit">Create Transformation</button>
                  <button
                    type="button"
                    onClick={() => setShowCreateBlogOverlay(false)}
                  >
                    Cancel
                  </button>
                </form>
              </div>
            </div>
          )}

          <div className="user-list">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div key={user._id} className="user-card">
                  <div className="user-details">
                    <span style={{ display: "flex" }}>
                      <img
                        className="user-details-img"
                        src={user.beforeImageUrl}
                      ></img>
                      <img
                        className="user-details-img"
                        src={user.afterImageUrl}
                      ></img>
                    </span>
                    <div>
                      <p style={{ textAlign: "start" }}>
                        <strong>Title:</strong> <span>{user.title}</span>
                      </p>
                      <p
                        style={{
                          textAlign: "start",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: "5",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        <strong>Description:</strong> {parse(user.description)}
                      </p>
                      <p style={{ textAlign: "start" }}>
                        <strong>Created on:</strong>{" "}
                        {new Date(user.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p>No Transformations Found</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Transformations;
